// React and External Libraries
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useFormik } from 'formik';
// PrimeReact Components
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { classNames } from 'primereact/utils';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
// APIs and Types
import { Organization } from 'apps/marsinet/src/app/typescript-axios';
// Models
import {
  Device,
  deviceLabels,
} from 'apps/marsinet/src/app/models/devicesModels';
import { getDeviceModelLabel } from 'apps/marsinet/src/app/utils/utils';
// Contexts
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
// Validations
import { useDeviceValidationSchema } from 'apps/marsinet/src/app/validations';
// Components
import ModalAddDeviceToOrganization from './components/modal-add-device-to-organization/modal-add-device-to-organization';
import { ProgressBarComponent } from 'apps/marsinet/src/app/components/progress-bar/progress-bar';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import {
  AvailableResourceActions,
  can,
  DEVICE_CATEGORIES,
  DeviceState,
  isAtlasModel,
} from '@marsinet/commons';
import { formatTime } from 'apps/marsinet/src/app/utils/utils';
import { normalizeApiError } from 'apps/marsinet/src/app/errors/normalize-api-error';
import { UpdateDeviceStateDto } from 'apps/marsinet/src/app/typescript-axios';

const ALL_DEVICE_STATES: DeviceState[] = Object.values(DEVICE_CATEGORIES).flat();  // Constante con todos los estados

const DeviceComponent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { device_id } = useParams<{ device_id: string }>();
  const { access_token, role, setRole, setAdmin, setError } =
    useContext(MarsinetContext);
  const { showToast: show } = useToast();
  const [device, setDevice] = useState<Device | null>(null);
  const [updateChanges, setUpdateChanges] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [institutions, setInstitutions] = useState<Organization[] | null>(null);
  const [isAtlas, setIsAtlas] = useState<boolean>(false);
  const hasDeviceDetailPageAccess =
    !!role && can(role, AvailableResourceActions.devices.view_detail_page);
  // Every editable field EXCEPT the state is covered by update_base
  const canEditDeviceModel =
    !!role && can(role, AvailableResourceActions.devices.update_base);
  // The state is managed by its own action / its own endpoint
  const canEditDeviceState =
    !!role && can(role, AvailableResourceActions.devices.update_state);
  const canAddDeviceToOrganization =
    !!role &&
    can(role, AvailableResourceActions.organizations.update_add_devices);
  const canRemoveDeviceFromOrganization =
    !!role &&
    can(role, AvailableResourceActions.organizations.update_del_devices);
  const canDeleteDevice =
    !!role && can(role, AvailableResourceActions.devices.delete);
  // Saving is worth offering if the user can change at least one of the two groups
  const canSaveDevice = canEditDeviceModel || canEditDeviceState;

  useEffect(() => {
    if (role && !hasDeviceDetailPageAccess) {
      navigate('/authenticated');
    }
  }, [role, hasDeviceDetailPageAccess, navigate]);

  const formik = useFormik({
    initialValues: {
      serial: '',
      model: 0,
      password: '',
      device_number: '',
      structure_version: '',
      device_state: 'enabled' as DeviceState,
    },
    validationSchema: useDeviceValidationSchema(),
    onSubmit: async (data) => {
      // Ensure model is a number before submit
      data.model = Number(data.model);
      await saveUpdateDevice(data);
    },
  });

  useEffect(() => {
    const fetchDeviceData = async () => {
      setLoading(true);

      try {
        if (!access_token || !device_id) return;

        axios.defaults.headers.common['Authorization'] =
          `Bearer ${access_token}`;

        const [deviceResponse, accountResponse, organizationsResponse] =
          await Promise.all([
            apiServices.devicesApi.devicesControllerFindOne(device_id),
            apiServices.myAccountApi.myAccountControllerGetData(),
            apiServices.devicesApi.devicesControllerFindAllOrganizations(
              device_id
            ),
          ]);

        const data_device: Device = {
          ...deviceResponse.data,
          model: Number(deviceResponse.data.model),
        };
        const isAtlasModelDevice = isAtlasModel(Number(data_device.model));
        setDevice(data_device);
        setIsAtlas(isAtlasModelDevice);

        formik.setValues({
          serial: data_device.serial,
          model: Number(data_device.model),
          structure_version: data_device.structure_version,
          device_number: data_device.device_number,
          password: data_device.password,
          device_state: data_device.device_state,
        });

        const accountData = accountResponse.data;
        setRole(accountData.organization?.role);
        setAdmin(accountData.can_edit);

        const organizationsData: Organization[] = organizationsResponse.data;

        setInstitutions(
          organizationsData.filter((org) => {
            const currentOrgName =
              accountData.organization?.name?.toLowerCase() || '';
            const orgName = org.name?.toLowerCase() || '';

            const isSameOrg = org.o_id === accountData.organization?.o_id;
            const isCurrentNotMarsi = !currentOrgName.includes('marsi');
            const isTargetMarsi = orgName.includes('marsi');
            const isMarsinet = orgName === 'marsinet';

            return (
              !isSameOrg && !isMarsinet && !(isCurrentNotMarsi && isTargetMarsi)
            );
          })
        );
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceData();
  }, [access_token, device_id, updateChanges]);

  /**
   * Persists the form.
   *
   * The state lives behind its own permission and its own endpoint, so it is sent
   * separately and only when it actually changed — otherwise a user holding only
   * `update_base` would get a 403 on every save.
   */
  const saveUpdateDevice = async (values: typeof formik.values) => {
    if (!device) return;

    const { device_state, ...deviceFields } = values;

    const shouldUpdateFields = canEditDeviceModel;
    const shouldUpdateState =
      canEditDeviceState && device_state !== device.device_state;

    if (!shouldUpdateFields && !shouldUpdateState) return;

    let anythingSaved = false;

    try {
      if (shouldUpdateFields) {
        await apiServices.devicesApi.devicesControllerUpdateDevice(
          deviceFields,
          device.d_id
        );
        anythingSaved = true;
      }

      if (shouldUpdateState) {
        await apiServices.devicesApi.devicesControllerUpdateDeviceState(
          {
            device_state:
              device_state as unknown as UpdateDeviceStateDto['device_state'],
          },
          device.d_id
        );
        anythingSaved = true;
      }

      show(
        'success',
        t('messages.success'),
        t('messages.devices.update_device')
      );
    } catch (error: unknown) {
      const normalizedError = normalizeApiError(error);
      show(
        'error',
        t('messages.error'),
        t(normalizedError.messageKey, {
          ...normalizedError.params,
          defaultValue: t('controlErrors.UNKNOWN_ERROR'),
        })
      );
    } finally {
      if (anythingSaved) {
        setUpdateChanges((previous) => !previous);
      }
    }
  };

  const addDeviceToInstitutionFunction = async (values: {
    organization_id: string;
  }) => {
    try {
      await apiServices.devicesApi.devicesControllerAddDeviceToOrganization(
        device.serial,
        values.organization_id
      );
      setUpdateChanges(!updateChanges);
      show(
        'success',
        t('messages.success'),
        t('messages.devices.add_device_to_org_success')
      );
    } catch (error: unknown) {
      const normalizedError = normalizeApiError(error);
      show(
        'error',
        t('messages.error'),
        t(normalizedError.messageKey, {
          ...normalizedError.params,
          defaultValue: t('controlErrors.UNKNOWN_ERROR'),
        })
      );
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiServices.devicesApi.devicesControllerRemoveDeviceFromOrganization(
        device_id,
        id
      );

      setUpdateChanges(!updateChanges);
      show(
        'success',
        t('messages.success'),
        t('messages.devices.delete_device_from_org_success')
      );
    } catch (error: unknown) {
      const normalizedError = normalizeApiError(error);
      show(
        'error',
        t('messages.error'),
        t(normalizedError.messageKey, {
          ...normalizedError.params,
          defaultValue: t('controlErrors.UNKNOWN_ERROR'),
        })
      );
    }
  };

  const handleDeleteDevice = async () => {
    try {
      await apiServices.devicesApi.devicesControllerRemove(device.d_id);
      show(
        'success',
        t('messages.success'),
        t('messages.devices.delete_device_success')
      );
      navigate('/authenticated/devices');
    } catch (error: unknown) {
      const normalizedError = normalizeApiError(error);
      show(
        'error',
        t('messages.error'),
        t(normalizedError.messageKey, {
          ...normalizedError.params,
          defaultValue: t('controlErrors.UNKNOWN_ERROR'),
        })
      );
    }

    // navigate('/authenticated/devices'); Estando aqui siempre va a navegar
  };

  const confirmDeleteDevice = () => {
    confirmDialog({
      message: t('messages.devices.confirm_delete_device'),
      header: t('messages.confirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('yes'),
      rejectLabel: t('no'),
      accept: handleDeleteDevice,
    });
  };

  const selectedState = formik.values.device_state;
  const statusDefinition = t(
    `pages.devices.statusInfo.${selectedState}.definition`,
    { defaultValue: '' }
  );
  const statusResponsible = t(
    `pages.devices.statusInfo.${selectedState}.responsible`,
    { defaultValue: '' }
  );
  const showStatusInfo = !!(statusDefinition || statusResponsible);

  if (!hasDeviceDetailPageAccess) {
    return null;
  }

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      {loading ? (
        <ProgressBarComponent />
      ) : (
        <>
          {device && (
            <div className="col-12 ">
              <div className="col-12 shadow-3 md:p-3 surface-card border-round-xl lg:h-full">
                <div className="p-3 flex align-items-center">
                  <Button
                    icon="pi pi-angle-left"
                    rounded
                    onClick={() => navigate('/authenticated/devices')}
                    severity="secondary"
                    className="mr-4"
                    aria-label="Bookmark"
                  />
                  <div className="mr-2">
                    <img src="/assets/robot.svg" alt="exo" loading="lazy" style={{ width: '35px' }} />
                  </div>
                  <div className="font-medium text-2xl text-700">{device.serial}</div>

                  <div className="flex align-items-center gap-2 ml-auto">
                    <Button
                      icon="pi pi-inbox"
                      label={t('pages.maintenance.ingestion.title')}
                      className="p-button-outlined"
                      onClick={() =>
                        navigate('/authenticated/maintenance', {
                          state: { tab: 'ingestion', serial: device.serial },
                        })
                      }
                    />
                    <Button
                      icon="pi pi-bell"
                      label={t('pages.maintenance.alarms.title')}
                      className="p-button-outlined"
                      onClick={() =>
                        navigate('/authenticated/maintenance', {
                          state: { tab: 'alarms', deviceId: device.d_id, serial: device.serial },
                        })
                      }
                    />
                    <Button
                      icon="pi pi-chart-bar"
                      label={t('pages.sessions.sessions')}
                      className="p-button-outlined"
                      onClick={() =>
                        navigate('/authenticated/sessions', {
                          state: { deviceId: device.d_id },
                        })
                      }
                    />
                  </div>
                </div>
                <div className="pt-5">
                  {loading ? (
                    <div className="text-center py-6">
                      <ProgressBar
                        mode="indeterminate"
                        style={{ height: '6px' }}
                      ></ProgressBar>
                    </div>
                  ) : (
                    <>
                      <form
                        autoComplete="off"
                        onSubmit={formik.handleSubmit}
                        className=""
                      >
                        <div className="grid formgrid p-fluid py-5">
                          <div className="col-12 mb-4">
                            <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                              <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                              <div className="px-2 z-1 surface-50 flex align-items-center">
                                <i className="pi pi-info-circle text-900 mr-2"></i>
                                <span className="text-900 font-bold">
                                  {t('pages.devices.devicePage.tabInfo')}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 md:col-6 field mb-5">
                            <label className="font-bold block mb-2">
                              {t('pages.devices.model')}
                              {!canEditDeviceModel &&
                                `: ${getDeviceModelLabel(device.model)}`}
                            </label>
                            {canEditDeviceModel && (
                              <Dropdown
                                id="input-edit-device-type"
                                name="model"
                                value={formik.values.model}
                                options={deviceLabels}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    'model',
                                    Number(e.value)
                                  )
                                }
                                optionLabel="label"
                                optionValue="value"
                                placeholder={t('pages.devices.model')}
                              />
                            )}
                            {formik.errors.model && (
                              <div className="flex align-items-center justify-content-start mt-1 mx-2">
                                <small className="p-error">
                                  *{t(formik.errors.model)}
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="col-12 md:col-6 field mb-5">
                            <label className="font-bold block mb-2">
                              {t('pages.devices.num_serial')}
                              {!canEditDeviceModel && `: ${device.serial}`}
                            </label>
                            {canEditDeviceModel && (
                              <InputText
                                id="input-edit-device-serial"
                                placeholder={t('pages.devices.num_serial')}
                                name="serial"
                                autoComplete="off"
                                value={formik.values.serial}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    'serial',
                                    e.target.value
                                  )
                                }
                                className={classNames({
                                  'p-invalid': formik.errors.serial,
                                })}
                              />
                            )}
                            {formik.errors.serial && (
                              <div className="flex align-items-center justify-content-start mt-1 mx-2">
                                <small className="p-error">
                                  *{t(formik.errors.serial)}
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="col-12 md:col-6 field mb-5">
                            <label className="font-bold block mb-2">
                              {t('pages.devices.deviceNumber')}
                              {!canEditDeviceModel &&
                                `: ${device.device_number}`}
                            </label>
                            {canEditDeviceModel && (
                              <InputText
                                id="input-edit-device-device_number"
                                placeholder={t('pages.devices.deviceNumber')}
                                name="device_number"
                                autoComplete="off"
                                value={formik.values.device_number}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    'device_number',
                                    e.target.value
                                  )
                                }
                                className={classNames({
                                  'p-invalid': formik.errors.device_number,
                                })}
                              />
                            )}
                            {formik.errors.serial && (
                              <div className="flex align-items-center justify-content-start mt-1 mx-2">
                                <small className="p-error">
                                  *{t(formik.errors.device_number)}
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="col-12 md:col-6 field mb-5 ">
                            <label className="font-bold block mb-2">
                              {t('pages.devices.structure_version')}
                              {!canEditDeviceModel &&
                                `: ${device.structure_version}`}
                            </label>
                            {canEditDeviceModel && (
                              <InputText
                                id="input-edit-device-structure_version"
                                placeholder={t(
                                  'pages.devices.structure_version'
                                )}
                                name="structure_version"
                                autoComplete="off"
                                value={formik.values.structure_version}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    'structure_version',
                                    e.target.value
                                  )
                                }
                                className={classNames({
                                  'p-invalid':
                                    formik.errors.structure_version,
                                })}
                              />
                            )}
                            {formik.errors.structure_version && (
                              <div className="flex align-items-center justify-content-start mt-1 mx-2">
                                <small className="p-error">
                                  *{t(formik.errors.structure_version)}
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="col-12 md:col-6 field mb-5">
                            <label className="font-bold block mb-2">
                              {t('pages.devices.selectStatus')}
                              {!canEditDeviceState &&
                                `: ${t(`pages.devices.statusInfo.${device.device_state}.name`)}`}
                            </label>

                            {canEditDeviceState && (
                              <Dropdown
                                id="input-edit-device-status"
                                value={formik.values.device_state}
                                options={ALL_DEVICE_STATES
                                  .map((status) => ({
                                    label: t(`pages.devices.statusInfo.${status}.name`),
                                    value: status,
                                  }))
                                  .sort((a, b) => a.label.localeCompare(b.label))}
                                onChange={(e) => formik.setFieldValue('device_state', e.value)}
                                placeholder={t('pages.devices.selectStatus')}
                                className={classNames({
                                  'p-invalid': formik.errors.device_state,
                                })}
                              />
                            )}

                            {canEditDeviceState && formik.errors.device_state && (
                              <div className="flex align-items-center justify-content-start mt-1 mx-2">
                                <small className="p-error">*{t(formik.errors.device_state)}</small>
                              </div>
                            )}

                            {showStatusInfo && (
                              <div className="surface-50 border-round-lg border-1 surface-border p-3 mt-3">
                                <div className="flex align-items-center mb-2">
                                  <i className="pi pi-info-circle text-primary mr-2"></i>
                                  <span className="font-bold text-900">
                                    {t(`pages.devices.statusInfo.${selectedState}.name`)}
                                  </span>
                                </div>
                                {statusDefinition && (
                                  <p className="m-0 mb-3 text-700 line-height-3">{statusDefinition}</p>
                                )}
                                {statusResponsible && (
                                  <div className="flex align-items-center text-600 text-sm">
                                    <i className="pi pi-user mr-2"></i>
                                    <span>
                                      {t('pages.devices.statusResponsible')}: <b>{statusResponsible}</b>
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Botón guardar (columna derecha, abajo del todo) */}
                          <div className="col-12 md:col-6 flex align-items-end justify-content-end mb-5 pr-6">
                            <Button
                              id="button-create-device"
                              severity="secondary"
                              icon="pi pi-save"
                              label={t('save')}
                              className="p-button-raised w-auto"
                              type="submit"
                              visible={canSaveDevice}
                            />
                          </div>


                          <div className="col-12 mt-6 mb-4">
                            <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                              <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                              <div className="px-2 z-1 surface-50 flex align-items-center">
                                <i className="pi pi-cog text-900 mr-2"></i>
                                <span className="text-900 font-bold">
                                  {t('pages.devices.softwareVersion')}
                                </span>
                              </div>
                            </div>
                          </div>

                          {!isAtlas ? (
                            <>
                              <div className="col-12 md:col-4 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.ceb_version')}:{' '}
                                  {device.ceb_version}
                                </label>
                              </div>
                              <div className="col-12 md:col-4 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.esp_version')}:{' '}
                                  {device.esp_version}
                                </label>
                              </div>
                              <div className="col-12 md:col-4 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.epos_version')}:{' '}
                                  {device.epos_version}
                                </label>
                              </div>
                              <div className="col-12 md:col-4 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.battery_cycles')}:{' '}
                                  {device.battery_cycles}
                                </label>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="col-12 md:col-3 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.mpb_version')}:{' '}
                                  {device.mpb_version}
                                </label>
                              </div>
                              <div className="col-12 md:col-3 field mb-5 mx-5 ">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.mcb_version')}:{' '}
                                  {device.mcb_version}
                                </label>
                              </div>
                              <div className="col-12 md:col-3 field mb-5 mx-5 ">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.scb_version')}:{' '}
                                  {device.scb_version}
                                </label>
                              </div>
                              <div className="col-12 md:col-3 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.total_device_steps')}:{' '}
                                  {device.total_device_steps}
                                </label>
                              </div>
                              <div className="col-12 md:col-3 field mb-5 mx-5">
                                <label className="font-bold block mb-2">
                                  {t('pages.devices.total_device_time')}:{' '}
                                  {formatTime(device.total_device_time)}
                                </label>
                              </div>
                            </>
                          )}
                        </div>
                      </form>
                      {(canAddDeviceToOrganization ||
                        canRemoveDeviceFromOrganization) && (
                          <>
                            <div className="col-12 mb-4">
                              <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                                <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                                <div className="px-2 z-1 surface-50 flex align-items-center">
                                  <i className="pi pi-building text-900 mr-2"></i>
                                  <span className="text-900 font-bold">
                                    {t('institution')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-center mb-4">
                              <Button
                                id="button-add-device-to-institution"
                                type="button"
                                label={t('pages.devices.devicePage.institution')}
                                className="p-button-raised"
                                onClick={() => setVisible(true)}
                                visible={canAddDeviceToOrganization}
                              ></Button>
                            </div>
                            {institutions?.length === 0 ? (
                              <div
                                className="flex flex-column align-items-center justify-content-center gap-2 text-center text-600 mb-4 py-5 surface-ground border-round-xl mx-auto w-full"
                                style={{ maxWidth: '40rem' }}
                              >
                                <i className="pi pi-building text-3xl text-400"></i>
                                <span className="font-medium">
                                  {t('pages.devices.devicePage.notInstitution')}
                                </span>
                              </div>
                            ) : (
                              <div
                                className="surface-card border-1 surface-border border-round-lg mb-4 mx-auto w-full overflow-hidden"
                                style={{ maxWidth: '40rem' }}
                              >
                                {institutions?.map((institution, index) => (
                                  <div
                                    key={institution.o_id}
                                    className={classNames(
                                      'flex align-items-center justify-content-between gap-3 px-3 py-2',
                                      { 'border-top-1 surface-border': index > 0 }
                                    )}
                                  >
                                    <div className="flex align-items-center gap-3 min-w-0">
                                      <i className="pi pi-building text-500"></i>
                                      <span className="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        {institution.name}
                                      </span>
                                    </div>
                                    <Button
                                      icon="pi pi-trash"
                                      rounded
                                      text
                                      severity="danger"
                                      onClick={() => handleDelete(institution.o_id)}
                                      tooltip={t('deleteInstitution')}
                                      tooltipOptions={{
                                        position: 'bottom',
                                        mouseTrack: true,
                                        mouseTrackTop: 15,
                                      }}
                                      aria-label="Delete"
                                      visible={canRemoveDeviceFromOrganization}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      {canDeleteDevice && (
                        <>
                          <div className="col-12 mb-4">
                            <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                              <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                              <div className="px-2 z-1 surface-50 flex align-items-center">
                                <i className="pi pi-trash text-900 mr-2"></i>
                                <span className="text-900 font-bold">
                                  {t('deleteDevice')}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-content-center my-4">
                            <Button
                              icon="pi pi-times"
                              label={t('deleteDevice')}
                              onClick={confirmDeleteDevice}
                              severity="danger"
                              aria-label="Eliminar"
                              visible={canDeleteDevice}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <ConfirmDialog />
          <ModalAddDeviceToOrganization
            visible={visible}
            setVisible={setVisible}
            addDeviceToInstitutionFunction={addDeviceToInstitutionFunction}
            device_id={device_id}
          />
        </>
      )}
    </div>
  );
};

export default DeviceComponent;